import house from "../assets/images/shutterstock_617467400-min.jpg";
import constants from "../constants/constants";
import { useState } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";

export default function Evaluation() {
  const [popup, setPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    propertyAddress: "",
    city: "",
    provinceState: "",
    propertySize: "",
    lotSize: "",
    propertyType: "",
    buildingType: "",
    notes: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  async function handleSubmit(event) {
    event.preventDefault();

    const submissionData = {
      ...formData,
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Lead Interested in Real Estate Reports",
    };

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      const data = await res.json();

      if (data?.success) {
        console.log("Success", data);
        setPopup({
          subject: "Thanks for your message!",
          message: "We will get back to you as soon as possible",
        });
      } else {
        setPopup({
          subject: "We encountered an error in sending this message",
          message: "Please try again later",
        });
      }
    } catch (err) {
      console.log(err);
      setPopup({
        subject: "We encountered an error in sending this message",
        message: "Please try again later",
      });
    }
  }

  const seoConfig = pageSEO.evaluation();
  return (
    <>
      <SEO {...seoConfig} />
      <section className="bg-summit-background min-h-screen pt-40 pb-20">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="max-w-7xl mx-auto px-6">

          {/* Header */}
          <div className="text-center mb-16">
            <motion.p
              variants={fadeUp}
              className="uppercase tracking-[5px] text-summit-highlight text-sm font-medium">
              Complimentary Service
            </motion.p>

            <motion.h1
              variants={fadeUp}
              className="font-heading text-5xl md:text-6xl text-summit-primary mt-4">
              Free Home Evaluation
            </motion.h1>

            <motion.div
              variants={fadeUp}
              className="w-20 h-1 bg-summit-highlight rounded-full mx-auto mt-6"></motion.div>

            <motion.p
              variants={fadeUp}
              className="max-w-2xl mx-auto mt-6 text-summit-muted leading-8">
              Curious about your home's current market value? Complete the form below
              and receive a complimentary home evaluation prepared using today's
              market data.
            </motion.p>
          </div>

          <motion.div
            variants={fadeUp}
            className="grid lg:grid-cols-[1.3fr_0.9fr] gap-12 items-start">
            <div className="bg-white rounded-3xl shadow-xl p-10">
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">Name*</label>
                  <input
                    required
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 outline-none transition focus:border-summit-highlight focus:bg-white"
                    placeholder="Full Name"
                  />
                </div>
                <div className="mb-4">
                  <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">Email*</label>
                  <input
                    required
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 outline-none transition focus:border-summit-highlight focus:bg-white"
                    placeholder="Email Address"
                  />
                </div>
                <div className="mb-4">
                  <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">Phone*</label>
                  <input
                    required
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 outline-none transition focus:border-summit-highlight focus:bg-white"
                    placeholder="Phone Number"
                  />
                </div>
                <div className="mb-4">
                  <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">Property Address*</label>
                  <input
                    required
                    name="propertyAddress"
                    value={formData.propertyAddress}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 outline-none transition focus:border-summit-highlight focus:bg-white"
                    placeholder="Address of the Property"
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-x-10">
                  <div className="mb-4">
                    <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">City*</label>
                    <input
                      required
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 outline-none transition focus:border-summit-highlight focus:bg-white"
                      placeholder="City of the Property"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">Province/State*</label>
                    <input
                      required
                      name="provinceState"
                      value={formData.provinceState}
                      onChange={handleInputChange}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 outline-none transition focus:border-summit-highlight focus:bg-white"
                      placeholder="Province/State of the Property"
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-x-10">
                  <div className="mb-4">
                    <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">Property Size*</label>
                    <input
                      required
                      name="propertySize"
                      value={formData.propertySize}
                      onChange={handleInputChange}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 outline-none transition focus:border-summit-highlight focus:bg-white"
                      placeholder="Approx. Square Footage"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">Lot Size*</label>
                    <input
                      required
                      name="lotSize"
                      value={formData.lotSize}
                      onChange={handleInputChange}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 outline-none transition focus:border-summit-highlight focus:bg-white"
                      placeholder="Approx. Square Footage/Acreage"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">Property Type*</label>
                  <select
                    required
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 outline-none transition focus:border-summit-highlight focus:bg-white"
                  >
                    <option value="">Please Select</option>
                    <option value="Residential">Residential</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Land">Land</option>
                    <option value="Parking">Parking</option>
                    <option value="Investment">Investment</option>
                    <option value="Agricultural">Agricultural</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">Building Type*</label>
                  <select
                    required
                    name="buildingType"
                    value={formData.buildingType}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 outline-none transition focus:border-summit-highlight focus:bg-white"
                  >
                    <option value="">Please Select</option>
                    <option value="House">House</option>
                    <option value="Townhouse">Townhouse</option>
                    <option value="Condo">Condo</option>
                    <option value="Duplex">Duplex</option>
                    <option value="Triplex">Triplex</option>
                    <option value="Office">Office</option>
                    <option value="Mobile Home">Mobile Home</option>
                    <option value="Retail">Retail</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">Other Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 outline-none transition focus:border-summit-highlight focus:bg-white resize-none h-32"
                    placeholder="Please tell me any other information you feel I should know about your property"
                  />
                </div>
                <button
                  type="submit"
                  className="btn-luxury rounded-xl self-start px-10 mt-3"
                >
                  Request Free Evaluation
                </button>
              </form>
            </div>

            <div className="sticky top-40">
              <div className="overflow-hidden rounded-3xl shadow-xl">
                <img
                  src={house}
                  alt="Free Home Evaluation"
                  className="w-full h-[700px] object-cover hover:scale-105 transition duration-700"
                />
              </div>

              <div className="bg-white rounded-3xl shadow-lg mt-6 p-8">
                <h3 className="font-heading text-3xl text-summit-primary">
                  Why Get an Evaluation?
                </h3>

                <p className="mt-4 text-summit-muted leading-8">
                  A professional home evaluation provides an estimate of your property's
                  current market value based on comparable sales, market trends, location,
                  and unique features. Whether you're planning to sell soon or simply want
                  to understand your home's worth, it's a valuable first step.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>
      {popup && (
        <>
          <div className="backdrop" />
          <div className="popup">
            <h5 className="font-bold text-black text-lg">{popup.subject}</h5>
            <p className="font-semibold text-med-grey tracking-wide mb-3">
              {popup.message}
            </p>
            <button onClick={() => setPopup(false)} className="btn-luxury">
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}
